// Sign Documents Page
// Displays list of documents awaiting user's signature with Sign Now CTA

import React, { useState, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { useTenant } from '@/lib/tenant/tenant-provider'
import ProtectedRoute from '@/components/ProtectedRoute'
import { SignDocumentButton } from '@/components/sign/SignDocumentButton'
import {
  FileText,
  Calendar,
  User,
  Search,
  Filter,
  CheckCircle,
  Clock,
  AlertCircle,
  AlertTriangle
} from 'lucide-react'
import {
  listSignDocuments,
  createSigningSession,
  getTenantSignConfig,
  handleSignApiError
} from '@/api/sign'
import type { SignDocument, SessionCreationResponse, TenantSignConfig } from '@/types/sign'
import { usePageTitle } from '@/stores/page-title-store'

function SignDocumentsContent() {
  const { user } = useAuth()
  const { tenant } = useTenant()
  usePageTitle('Assinatura de Documentos')
  const [documents, setDocuments] = useState<SignDocument[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedStatus, setSelectedStatus] = useState<string>('all')
  const [tenantConfig, setTenantConfig] = useState<TenantSignConfig | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [selectedSession, setSelectedSession] = useState<SessionCreationResponse | null>(null)

  // Load tenant configuration
  useEffect(() => {
    const loadTenantConfig = async () => {
      if (!tenant?.tenantId) return

      try {
        const config = await getTenantSignConfig(tenant.tenantId)
        setTenantConfig(config)
      } catch (err) {
        console.error('Error loading tenant config:', err)
        // Fallback to default config
        setTenantConfig({ instantSigningEnabled: false })
      }
    }

    loadTenantConfig()
  }, [tenant?.tenantId])

  // Load documents function
  const loadDocuments = async () => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await listSignDocuments({
        status: selectedStatus === 'all' ? undefined : selectedStatus
      })
      setDocuments(response.items)
    } catch (err) {
      const apiError = handleSignApiError(err)
      setError(apiError.message)
      console.error('Error loading sign documents:', err)
    } finally {
      setIsLoading(false)
    }
  }

  // Load documents on mount and when status changes
  useEffect(() => {
    loadDocuments()
  }, [selectedStatus])

  // Filter documents by search term
  const filteredDocuments = documents.filter((doc) =>
    doc.title.toLowerCase().includes(searchTerm.toLowerCase())
  )

  // Calculate statistics
  const documentStats = {
    total: documents.length,
    pending: documents.filter((d) =>
      d.signers.some(
        (s) =>
          (s.userId === user?.id || s.email === user?.email) && s.status === 'pending'
      )
    ).length,
    inProgress: documents.filter((d) =>
      d.signers.some(
        (s) =>
          (s.userId === user?.id || s.email === user?.email) && s.status === 'in_progress'
      )
    ).length,
    completed: documents.filter((d) =>
      d.signers.some(
        (s) =>
          (s.userId === user?.id || s.email === user?.email) && s.status === 'completed'
      )
    ).length
  }

  // Handle sign button click
  const handleSignClick = async (documentId: string, signerId: string) => {
    if (!tenant?.tenantId) {
      setError('Tenant information is missing')
      return
    }

    setError(null)

    try {
      const session = await createSigningSession({
        documentId,
        signerId,
        tenantId: tenant.tenantId
      })

      // Store session for modal routing
      setSelectedSession(session)

      // Route to appropriate modal based on requiresOtp
      if (session.requiresOtp) {
        // TODO: Open OTP verification modal (Story 3.2)
        console.log('Opening OTP modal for session:', session.sessionId)
        alert('OTP verification required. This will be implemented in Story 3.2')
      } else {
        // TODO: Open instant signature modal (Story 3.2)
        console.log('Opening instant signature modal for session:', session.sessionId)
        alert('Instant signing ready! This will be implemented in Story 3.2')
      }
    } catch (err) {
      const apiError = handleSignApiError(err)
      setError(apiError.message)
      console.error('Error creating signing session:', err)
    }
  }

  // Retry loading documents
  const handleRetry = () => {
    setError(null)
    loadDocuments()
  }

  if (isLoading) {
    return (
      
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-brand-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-brand-gray-light font-body">Loading documents...</p>
          </div>
        </div>
      
    )
  }

  return (
    
      <div className="space-y-8 animate-fade-in-up">
        {/* Stats Cards */}
        <div className="dashboard-grid lg:grid-cols-4">
          <div className="card-hover">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-monumenta bg-brand-primary/10">
                <FileText className="w-6 h-6 text-brand-primary" />
              </div>
            </div>
            <div className="space-y-2">
              <div className="text-3xl font-display font-bold text-white">
                {documentStats.total}
              </div>
              <p className="text-sm text-brand-gray">Total Documents</p>
            </div>
          </div>

          <div className="card-hover">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-monumenta bg-warning/10">
                <Clock className="w-6 h-6 text-warning" />
              </div>
            </div>
            <div className="space-y-2">
              <div className="text-3xl font-display font-bold text-white">
                {documentStats.pending}
              </div>
              <p className="text-sm text-brand-gray">Pending Signature</p>
            </div>
          </div>

          <div className="card-hover">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-monumenta bg-brand-blue/10">
                <AlertCircle className="w-6 h-6 text-brand-blue" />
              </div>
            </div>
            <div className="space-y-2">
              <div className="text-3xl font-display font-bold text-white">
                {documentStats.inProgress}
              </div>
              <p className="text-sm text-brand-gray">In Progress</p>
            </div>
          </div>

          <div className="card-hover">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-monumenta bg-success/10">
                <CheckCircle className="w-6 h-6 text-success" />
              </div>
            </div>
            <div className="space-y-2">
              <div className="text-3xl font-display font-bold text-white">
                {documentStats.completed}
              </div>
              <p className="text-sm text-brand-gray">Completed</p>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="card bg-error/10 border-error/30">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-6 h-6 text-error flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h3 className="font-display font-bold text-white mb-2">Error</h3>
                <p className="text-brand-gray-light mb-4">{error}</p>
                <button onClick={handleRetry} className="btn-secondary text-sm">
                  Retry
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="card">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-brand-gray w-5 h-5" />
              <input
                type="text"
                placeholder="Search documents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10"
              />
            </div>

            {/* Status Filter */}
            <div className="lg:w-48">
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-brand-gray w-5 h-5" />
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="input-field pl-10 appearance-none"
                >
                  <option value="all">All Status</option>
                  <option value="sent">Sent</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Documents List */}
        <div className="card">
          <h2 className="font-display text-2xl font-bold text-white mb-6">
            Documents ({filteredDocuments.length})
          </h2>

          {filteredDocuments.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-brand-gray mx-auto mb-4" />
              <h3 className="font-display text-xl font-bold text-white mb-2">
                No documents found
              </h3>
              <p className="text-brand-gray-light">
                {searchTerm || selectedStatus !== 'all'
                  ? 'Try adjusting your search filters'
                  : 'You have no documents requiring your signature'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredDocuments.map((doc) => {
                const currentUserSigner = doc.signers.find(
                  (s) => s.userId === user?.id || s.email === user?.email
                )

                return (
                  <div
                    key={doc.documentId}
                    className="card-interactive p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:border-brand-primary/30 transition-all duration-200"
                  >
                    <div className="flex items-start gap-4 flex-1">
                      <div className="p-3 rounded-monumenta bg-brand-dark-lighter text-brand-primary">
                        <FileText className="w-5 h-5" />
                      </div>

                      <div className="flex-1 min-w-0">
                        <h3 className="font-display font-bold text-white mb-1 truncate">
                          {doc.title}
                        </h3>
                        {doc.description && (
                          <p className="text-sm text-brand-gray-light mb-2 line-clamp-2">
                            {doc.description}
                          </p>
                        )}
                        <div className="flex flex-wrap items-center gap-3 text-sm text-brand-gray">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            Created {new Date(doc.createdAt).toLocaleDateString('en-US')}
                          </span>
                          <span className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            {doc.signers.length} signer{doc.signers.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      {/* Status Badge */}
                      {currentUserSigner && (
                        <div
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            currentUserSigner.status === 'completed'
                              ? 'bg-success/20 text-success'
                              : currentUserSigner.status === 'in_progress'
                                ? 'bg-brand-blue/20 text-brand-blue'
                                : currentUserSigner.status === 'pending'
                                  ? 'bg-warning/20 text-warning'
                                  : 'bg-brand-gray/20 text-brand-gray'
                          }`}
                        >
                          {currentUserSigner.status === 'completed'
                            ? 'Signed'
                            : currentUserSigner.status === 'in_progress'
                              ? 'In Progress'
                              : currentUserSigner.status === 'pending'
                                ? 'Pending'
                                : currentUserSigner.status}
                        </div>
                      )}

                      {/* Sign Button */}
                      <SignDocumentButton
                        document={doc}
                        currentUserId={user?.id}
                        isAuthenticated={!!user}
                        instantSigningEnabled={tenantConfig?.instantSigningEnabled ?? false}
                        onSignClick={handleSignClick}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Help Section */}
        <div className="card bg-brand-blue/5 border-brand-blue/20">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-monumenta bg-brand-blue/20">
              <AlertTriangle className="w-6 h-6 text-brand-blue" />
            </div>
            <div>
              <h3 className="font-display text-lg font-bold text-white mb-2">Need Help?</h3>
              <p className="text-brand-gray-light mb-4">
                If you have questions about signing documents or need assistance, please contact
                support.
              </p>
              <button className="btn-secondary text-sm">Contact Support</button>
            </div>
          </div>
        </div>
      </div>
    
  )
}

export default function SignDocumentsPage() {
  return (
    <ProtectedRoute>
      <SignDocumentsContent />
    </ProtectedRoute>
  )
}
